import React, { useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, Download, CornerUpLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const resolve = (u) => {
  if (!u) return null;
  if (/^(https?:|blob:|data:)/i.test(u)) return u;
  return `${BACKEND_URL}${u}`;
};

/**
 * Full-screen image lightbox with gallery browsing.
 *
 * Props:
 *   open: bool
 *   images: [{ id, media_url, media_filename, caption, created_at, direction, wa_message_id, msg }]
 *   index: int (current index into `images`)
 *   onChangeIndex: (newIndex) => void
 *   onClose: () => void
 *   onReply: (msg) => void  // optional
 */
const ChatLightbox = ({ open, images, index, onChangeIndex, onClose, onReply }) => {
  const total = images?.length || 0;
  const current = total > 0 ? images[Math.max(0, Math.min(index, total - 1))] : null;

  const goPrev = useCallback(() => {
    if (total <= 1) return;
    onChangeIndex((index - 1 + total) % total);
  }, [index, total, onChangeIndex]);

  const goNext = useCallback(() => {
    if (total <= 1) return;
    onChangeIndex((index + 1) % total);
  }, [index, total, onChangeIndex]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
    };
    document.addEventListener('keydown', onKey);
    // Lock background scroll
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose, goPrev, goNext]);

  if (!open || !current) return null;

  const src = resolve(current.media_url);
  const filename = current.media_filename || `image-${current.id || index}.jpg`;
  const handleBackdrop = (e) => { if (e.target === e.currentTarget) onClose(); };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/90 flex flex-col items-center justify-center select-none"
      onClick={handleBackdrop}
      data-testid="chat-lightbox"
    >
      {/* Top action bar */}
      <div className="absolute top-0 left-0 right-0 px-4 py-3 flex items-center gap-3 bg-gradient-to-b from-black/70 to-transparent">
        <p className="flex-1 text-white text-sm font-medium truncate" data-testid="lightbox-counter">
          {filename}
          {total > 1 && <span className="opacity-60 ml-2">{index + 1} / {total}</span>}
        </p>
        <a
          href={src}
          download={filename}
          target="_blank"
          rel="noopener noreferrer"
          className="p-2 rounded-full hover:bg-white/10 text-white"
          title="Download"
          data-testid="lightbox-download-btn"
          onClick={(e) => e.stopPropagation()}
        >
          <Download className="h-5 w-5" />
        </a>
        {onReply && current.msg?.wa_message_id && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onReply(current.msg); onClose(); }}
            className="p-2 rounded-full hover:bg-white/10 text-white"
            title="Reply"
            data-testid="lightbox-reply-btn"
          >
            <CornerUpLeft className="h-5 w-5" />
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          className="p-2 rounded-full hover:bg-white/10 text-white"
          title="Close (Esc)"
          data-testid="lightbox-close-btn"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Prev / next arrows */}
      {total > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            className="absolute left-3 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/40 hover:bg-black/60 text-white"
            title="Previous (←)"
            data-testid="lightbox-prev-btn"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/40 hover:bg-black/60 text-white"
            title="Next (→)"
            data-testid="lightbox-next-btn"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </>
      )}

      {/* Image */}
      <div className="flex-1 w-full flex items-center justify-center px-12 py-16 overflow-hidden">
        <img
          src={src}
          alt={filename}
          className="max-w-full max-h-full object-contain rounded shadow-2xl"
          data-testid="lightbox-img"
        />
      </div>

      {/* Caption */}
      {current.caption && (
        <div className="absolute bottom-0 left-0 right-0 px-6 py-4 bg-gradient-to-t from-black/80 to-transparent">
          <p className="text-white text-center text-sm" data-testid="lightbox-caption">{current.caption}</p>
        </div>
      )}
    </div>
  );
};

export default ChatLightbox;
